import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ActivityPanel } from './ActivityPanel';
import { PhotographerDetailsPanel } from './PhotographerDetailsPanel';
import { loadTags, showNewTagsModal } from '../../redux/modules/tags';
import { TagsPanel } from '../../components/panels/TagsPanel/TagsPanel';
import { useSelector, useSettingsSelector } from 'src/client/redux';
import { PanelsGrid } from './PanelsGrid';
import { Photographer } from 'src/@types/Photographer';

type Props = {
  className?: string;
  photographer: Photographer;
};

export default function PhotographerPanels({ photographer, className }: Props) {
  const dispatch = useDispatch();

  const tags = useSelector((state) => state.tags.tags);
  const showTags = useSettingsSelector('tags');
  const ratingsScale = useSettingsSelector<number>('ratingsScale');
  const user = useSelector((state) => state.auth.user);

  const userTags = tags[photographer.UUID] ? tags[photographer.UUID].tags : [];
  const combinedTags = [...userTags, ...photographer.uniqueTagsWithUrl];

  const [fullMode, setFullMode] = useState(false);

  useEffect(() => {
    if (!user) return;

    setFullMode(user.validSubscription);

    dispatch(loadTags({ objectUUID: photographer.UUID, type: 'PHOTOGRAPHER' }));
  }, [dispatch, photographer.UUID, user]);

  const addNewTag = useCallback(() => {
    if (!user) return;

    dispatch(showNewTagsModal({ name: photographer.name, objectUUID: photographer.UUID, objectType: 'photographer' }));
  }, [dispatch, photographer.name, photographer.UUID, user]);

  return (
    <PanelsGrid className={className} data-testid="photographer-panels">
      <PhotographerDetailsPanel photographer={photographer} className="photographer-details" />

      <ActivityPanel
        className="photographer-activity"
        ratingAverage={parseFloat(photographer.ratingAverage.toFixed(ratingsScale))}
        objectUUID={photographer.UUID}
        objectType="photographer"
        ratings={parseFloat(photographer.ratingCount.toFixed(ratingsScale))}
        views={photographer.views}
        itemUrl={photographer.path}
        fullMode={fullMode}
      />

      {!!showTags && <TagsPanel tags={combinedTags} userTags={userTags} onAddNewTag={addNewTag} />}
    </PanelsGrid>
  );
}
